import { useEffect, useState } from "react";
import type React from "react";
import { useAuth } from "../../context/AuthContext";
import { Roles } from "../../constants/roles";

import STStandApi from "../../api/StationaryTesting/STStand";
import STStandTypeApi from "../../api/StationaryTesting/STStandType";
import STFacility from "../../api/StationaryTesting/STFacility";

import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import ModalSimpleButton from "../../components/Modal/ModalSimpleButton";
import { GlobalSecondaryButton } from "../styles";

import * as Yup from "yup";
import { Form, Formik, FormikValues } from "formik";

interface TestStandInfo {
  standName: string;
  isActive: boolean;
  standTypeName: string;
  etqid: string | null;
  stationaryTestFacilityName: string;
}

const initialTestStandInfo = {
  standName: "",
  isActive: false,
  standTypeName: "",
  etqid: "",
  stationaryTestFacilityName: "",
};

const testStandValidationSchema = () =>
  Yup.object().shape({
    standName: Yup.string().required("Test Stand Name is required"),
    standTypeName: Yup.string().required("Test Stand Type is required"),
    stationaryTestFacilityName: Yup.string().required("Test Stand Facility is required"),
    etqid: Yup.string().nullable().optional(),
    isActive: Yup.boolean().optional(),
  });

const TestStandManagement = () => {
  const { hasAnyRole } = useAuth();
  const canManageTestStand = hasAnyRole([Roles.StationaryTestingWorker, Roles.Developer]);

  const [isEditing, setIsEditing] = useState(false);
  const [isTestStandCreating, setIsTestStandCreating] = useState(false);
  const [isTestStandUpdating, setIsTestStandUpdating] = useState(false);
  const [showTestStandInfo, setShowTestStandInfo] = useState(false);

  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{ title: string; message: string }>({
    title: "",
    message: "",
  });
  const [testStandInfo, setTestStandInfo] = useState<TestStandInfo>(initialTestStandInfo);
  const [standTypes, setStandTypes] = useState<string[]>([]);
  const [testStands, setTestStands] = useState<TestStandInfo[]>([]);
  const [standFacilities, setStandFacilities] = useState<string[]>([]);

  useEffect(() => {
    getAllTestStands();
    getAllTestStandTypes();
    getAllActiveTestStandFacilities();
  }, []);

  const getAllTestStands = async () => {
    const response = await STStandApi.getAll();
    setTestStands(response);
  };

  const getAllActiveTestStandFacilities = async () => {
    const response = await STFacility.getAllActives();
    const formattedData = response.map((facility: any) => facility?.stationaryTestFacilityName);
    setStandFacilities(formattedData);
  };

  const getAllTestStandTypes = async () => {
    const response = await STStandTypeApi.getAllActives();
    const formattedData = response.map((type: any) => type?.standTypeName);
    setStandTypes(formattedData);
  };

  const handleSelectTestStandOnChange = (
    e: React.SyntheticEvent,
    value: string | null,
    reason: string,
  ) => {
    if (reason === "clear") {
      setShowTestStandInfo(false);
      setTestStandInfo(initialTestStandInfo);
      setIsEditing(false);
      setIsTestStandCreating(false);
      setIsTestStandUpdating(false);
      return;
    }

    if (!value) {
      return;
    }

    const foundTestStand = testStands.find((stand: any) => stand?.standName === value);
    if (foundTestStand) {
      setTestStandInfo(foundTestStand);
      setShowTestStandInfo(true);
      setIsTestStandCreating(false);
      setIsTestStandUpdating(true);
      setIsEditing(true);
    }
  };

  const handleAddNewTestStandOnClick = () => {
    setTestStandInfo((prevInitialTestStandInfo) => ({
      ...prevInitialTestStandInfo,
      isActive: true,
    }));
    setShowTestStandInfo(true);
    setIsTestStandCreating(true);
    setIsTestStandUpdating(false);
    setIsEditing(true);
  };

  const handleOnSubmit = async (values: FormikValues): Promise<void> => {
    setIsEditing(false);

    try {
      if (isTestStandCreating) {
        await STStandApi.createTestStand(values);
      } else if (isTestStandUpdating) {
        await STStandApi.updateTestStand(values);
      }

      setModalContent({
        title: "Success!",
        message: `Test Stand ${isTestStandCreating ? "created" : "updated"} successfully!`,
      });
      setIsMessageModalOpen(true);
    } catch (error: Error | any) {
      console.error("Error during form submission:", error);
      setModalContent({
        title: "Error!",
        message: error.message || "An error occurred during form submission.",
      });
      setIsMessageModalOpen(true);
    } finally {
      setShowTestStandInfo(false);
      setTestStandInfo(initialTestStandInfo);
      getAllTestStands();
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={3} marginBottom={4}>
          <Autocomplete
            noOptionsText="Loading Test Stands..."
            value={testStandInfo.standName || null}
            onChange={handleSelectTestStandOnChange}
            options={testStands.map((stand) => stand?.standName)}
            sx={{ width: "35rem" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Test Stand" required={isTestStandUpdating} />
            )}
            disablePortal
            autoSelect
            size="small"
          />
          {canManageTestStand && (
            <GlobalSecondaryButton
              variant="contained"
              type="button"
              onClick={handleAddNewTestStandOnClick}
              disabled={showTestStandInfo}>
              Add New Test Stand
            </GlobalSecondaryButton>
          )}
        </Box>

        {showTestStandInfo && (
          <Formik
            initialValues={testStandInfo}
            onSubmit={handleOnSubmit}
            validationSchema={() => testStandValidationSchema()}
            enableReinitialize={true}>
            {({ values, handleChange, handleBlur, setFieldValue, isValid, dirty }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.standName}
                    disabled={!isTestStandCreating || !canManageTestStand}
                    name="standName"
                    label="Test Stand Name"
                    size="small"
                    style={{ width: "35rem" }}
                    required={isTestStandCreating}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    width={"35rem"}>
                    <Autocomplete
                      noOptionsText="Loading Stand Types..."
                      value={values.standTypeName}
                      onChange={(_, value) => setFieldValue("standTypeName", value ?? "")}
                      disabled={!canManageTestStand}
                      isOptionEqualToValue={(option, value) => value === option}
                      disablePortal
                      options={standTypes}
                      renderInput={(params) => (
                        <TextField {...params} label="Test Stand Type" required size="small" />
                      )}
                      autoSelect
                      style={{ width: "75%" }}
                    />

                    <FormControlLabel
                      label="Active"
                      checked={values.isActive}
                      name="isActive"
                      disabled={!canManageTestStand}
                      onChange={handleChange}
                      control={<Checkbox />}
                    />
                  </Box>
                  <Autocomplete
                    noOptionsText="Loading Stand Facilities..."
                    value={values.stationaryTestFacilityName}
                    onChange={(_, value) =>
                      setFieldValue("stationaryTestFacilityName", value ?? "")
                    }
                    isOptionEqualToValue={(option, value) => value === option}
                    disabled={!canManageTestStand}
                    disablePortal
                    options={standFacilities}
                    renderInput={(params) => (
                      <TextField {...params} label="Test Stand Facility" size="small" required />
                    )}
                    autoSelect
                    style={{ width: "35rem" }}
                  />
                  <TextField
                    onChange={handleChange}
                    name="etqid"
                    disabled={!canManageTestStand}
                    value={values.etqid ?? ""}
                    label="ETQ ID"
                    size="small"
                    style={{ width: "35rem" }}
                  />
                </Box>
                <Box display="flex" gap={2} alignItems="center" marginTop={2}>
                  {isEditing && (
                    <GlobalSecondaryButton
                      variant="contained"
                      disabled={!isEditing || !canManageTestStand || !dirty || !isValid}
                      type="submit">
                      Save
                    </GlobalSecondaryButton>
                  )}
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
      {isMessageModalOpen && (
        <ModalSimpleButton
          title={modalContent!.title}
          buttonText="Close"
          buttonAction={() => {
            setIsMessageModalOpen(false);
            setModalContent({ title: "", message: "" });
          }}
          setOpen={setIsMessageModalOpen}
          open={isMessageModalOpen}>
          <Typography style={{ padding: "0 16px", fontSize: 18, textAlign: "center" }}>
            {modalContent!.message}
          </Typography>
        </ModalSimpleButton>
      )}
    </>
  );
};

export default TestStandManagement;
